import { type CSSProperties, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../components/atoms/modal";
import GetHeroIcon from "../../utils/getHeroIcons";

import QuestionHeader from "./_components/question/questionHeader";
import SectionSummaryModal from "./_components/question/sectionSummaryModal";
import SidebarQuestionBlock from "./_components/question/sidebarQuestionBlock";
import AssessmentCodingQuestion from "./_components/question/variants/coding-question/assessmentCodingQuestion";
import AssessmentObjectiveQuestions from "./_components/question/variants/objective-question/assessmentObjectiveQuestions";

import { useAppDispatch, useAppSelector } from "../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useLazyHeartBeatApiQuery,
  useTimeSpentMutation
} from "@assessment/store/slices/api/assessmentApiSlice";

import { cn } from "@assessment/utils/cn";
import { useShowErrors } from "./_components/toasterWrapper";
import AssessmentSubjectiveQuestion from "./_components/question/variants/subjective-question/assessmentSubjectiveQuestion";
import { useParams } from "react-router-dom";

const Question = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { scheduleId } = useParams();

  const showErrors = useShowErrors();

  const [timeSpent] = useTimeSpentMutation();
  const [heartBeatApi] = useLazyHeartBeatApiQuery();

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const assessmentData = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData
  );

  const assessmentProgress = useAppSelector(
    (state) => state.AssessmentSlice.assessmentProgress
  );

  const preAssessmentData = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData
  );

  const showSummaryModal = useAppSelector(
    (store) => store.AssessmentSlice.modalFlags.showSummaryModal
  );
  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  const timeSpentDetails = useAppSelector(
    (store) => store.AssessmentSlice.timeSpentDetails
  );

  const [disableSidebarQuestionBlocks, setDisableSidebarQuestionBlocks] =
    useState(false);

  const questionEnterTimeRef = useRef<number | null>(null);
  const sectionEnterTimeRef = useRef<number | null>(null);
  const currentQuestionIdRef = useRef<string | null>(null);
  const questionLayoutRef = useRef<HTMLDivElement | null>(null);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [availableViewportHeight, setAvailableViewportHeight] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    const updateAvailableViewportHeight = () => {
      if (!questionLayoutRef.current) return;

      const topOffset = questionLayoutRef.current.getBoundingClientRect().top;
      const bottomSpacing = window.innerWidth >= 1024 ? 24 : 16;
      const nextHeight = Math.max(
        window.innerHeight - topOffset - bottomSpacing,
        320
      );

      setAvailableViewportHeight(nextHeight);
    };

    updateAvailableViewportHeight();
    window.addEventListener("resize", updateAvailableViewportHeight);
    window.addEventListener("orientationchange", updateAvailableViewportHeight);

    return () => {
      window.removeEventListener("resize", updateAvailableViewportHeight);
      window.removeEventListener(
        "orientationchange",
        updateAvailableViewportHeight
      );
    };
  }, []);

  if (
    !activeQuestionMeta?.sectionId ||
    !activeQuestionMeta.questionId ||
    !assessmentData ||
    !preAssessmentData
  )
    return;

  const ongoingSections = assessmentData?.ongoingSections;

  if (!ongoingSections || ongoingSections.length === 0) return;

  const targetSection = ongoingSections.find(
    (section) => section.sectionId === activeQuestionMeta.sectionId
  );

  if (!targetSection) return;

  const targetQuestion = targetSection.questions.find(
    (question) => question.questionId === activeQuestionMeta.questionId
  );

  if (!targetQuestion) return;

  const questionType = targetQuestion.type;

  const changeActiveQuestion = async (variant: "next" | "previous") => {
    // await updateTimeSpentHandler();
    const newQuestionNumber =
      variant === "next"
        ? targetQuestion.orderInSection + 1
        : targetQuestion.orderInSection - 1;
    const newQuestionId = targetSection.questions.find(
      (el) => el.orderInSection === newQuestionNumber
    )?.questionId;
    if (!newQuestionId) return;

    dispatch(
      AssessmentSliceActions.mutateActiveQuestionMeta({
        questionId: newQuestionId,
        sectionId: targetSection.sectionId
      })
    );
  };

  useEffect(() => {
    // heartBeatApiCall();
    return () => {
      if (
        currentQuestionIdRef.current !== null &&
        questionEnterTimeRef.current !== null
      ) {
        const delta = Math.floor(
          (Date.now() - questionEnterTimeRef.current) / 1000
        );
        const timeSpentOnPreviousQuestion =
          timeSpentDetails[targetSection.sectionId].questions[
            targetQuestion.questionId
          ].value + delta;
        dispatch(
          AssessmentSliceActions.mutateTimeSpentOnQuestion({
            questionId: targetQuestion.questionId,
            sectionId: targetSection.sectionId,
            value: timeSpentOnPreviousQuestion
          })
        );
      }
    };
  }, []);

  useEffect(() => {
    dispatch(AssessmentSliceActions.mutateQuestionType(questionType));
  }, [questionType]);

  // const heartBeatApiCall = async () => {
  //   try {
  //     if (!scheduleId) {
  //       throw {
  //         status: "",
  //         title: "Schedule Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     if (!ongoingId) {
  //       throw {
  //         status: "",
  //         title: "Ongoing Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     const heartBeatRes = await heartBeatApi({
  //       ongoingId: ongoingId,
  //       scheduleId: scheduleId
  //     });
  //     if (heartBeatRes?.data?.data?.ongoingQPDuration) {
  //       dispatch(
  //         AssessmentSliceActions.mutateTimeLeft(
  //           heartBeatRes?.data?.data?.ongoingQPDuration
  //         )
  //       );
  //     }
  //   } catch (error: any) {
  //     console.log("error", error);
  //   }
  // };
  return (
    <>
      <div className="flex flex-col w-full min-h-screen md:min-h-screen p-3 md:p-6 mb-6">
        {/* Top */}
        <QuestionHeader
          assessmentTitle={preAssessmentData.title}
          sectionTitle={targetSection.title}
          qpInstruction={preAssessmentData.qpInstruction}
          scheduleInstruction={preAssessmentData.scheduleInstruction}
          onToggleQuestionPanel={() => setIsMobileSidebarOpen((prev) => !prev)}
          isQuestionPanelOpen={isMobileSidebarOpen}
        />
        {/* Main */}
        <div
          ref={questionLayoutRef}
          style={
            {
              ...(availableViewportHeight
                ? { "--question-layout-height": `${availableViewportHeight}px` }
                : {})
            } as CSSProperties
          }
          className="relative mt-4 flex w-full flex-col items-stretch gap-3 overflow-visible md:mt-8 md:gap-5 lg:min-h-0 lg:flex-1 lg:flex-row lg:h-[var(--question-layout-height)] lg:max-h-[var(--question-layout-height)]"
        >
          {/* Mobile Overlay Backdrop */}
          {isMobileSidebarOpen && (
            <div
              className="fixed inset-0 lg:hidden bg-black/50 z-20"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={cn(
              "flex flex-col flex-none bg-secondary-bg border border-secondary/10 dark:border-0",
              "transition-all duration-300 ease-in-out",
              // Mobile positioning
              "lg:z-[40] lg:rounded-2xl lg:self-stretch lg:min-h-0 lg:overflow-hidden",
              isCollapsed
                ? "lg:w-[96px]"
                : "lg:w-[18%] lg:min-w-[240px] lg:max-w-[340px]",
              isMobileSidebarOpen
                ? "fixed left-0 top-0 bottom-0 h-[100dvh] w-full max-w-sm z-30 rounded-none shadow-xl"
                : "hidden lg:flex",
              "max-lg:w-full"
            )}
            data-tour="acq-sidebar"
          >
            <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-secondary/10 bg-secondary-bg sticky top-0 z-10">
              <p className="text-base font-semibold text-secondary">
                {t("assessment.question.questions")}
              </p>
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-secondary/10"
                aria-label={t("assessment.question.closeQuestionsPanel")}
              >
                <GetHeroIcon
                  name="XMarkIcon"
                  className="w-5 h-5"
                  aria-hidden="true"
                  variant="outline"
                />
              </button>
            </div>
            <div
              className={
                isMobileSidebarOpen || !isCollapsed
                  ? cn(
                      `grid gap-3 md:gap-4 p-4 md:p-5 overflow-y-auto thin-scrollbar place-content-start flex-1 min-h-0 [grid-template-columns:repeat(auto-fit,minmax(3.5rem,1fr))]`
                    )
                  : "p-4 md:p-5 space-y-3 md:space-y-4 overflow-y-auto overflow-x-hidden thin-scrollbar flex-1 min-h-0"
              }
            >
              {targetSection.questions.map((question) => {
                const currentStatus =
                  assessmentProgress[targetSection.sectionId].questions[
                    question.questionId
                  ].status;

                const questionStatus =
                  activeQuestionMeta.questionId === question.questionId
                    ? currentStatus === "completed"
                      ? "viewing_completed"
                      : "viewing"
                    : currentStatus;
                const questionFlagged =
                  assessmentProgress[targetSection.sectionId].questions[
                    question.questionId
                  ].flagged;
                return (
                  <div
                    key={question.id}
                    className="min-w-0 flex justify-center"
                  >
                    <SidebarQuestionBlock
                      questionId={question.questionId}
                      sectionId={targetSection.sectionId}
                      number={question.orderInSection}
                      status={questionStatus}
                      flagged={questionFlagged}
                      questionType={question.type}
                      questionSubType={question.subType}
                      disableSidebarQuestionBlocks={
                        disableSidebarQuestionBlocks
                      }
                      setDisableSidebarQuestionBlocks={
                        setDisableSidebarQuestionBlocks
                      }
                      targetSection={targetSection}
                      onQuestionSwitched={() => setIsMobileSidebarOpen(false)}
                    />
                  </div>
                );
              })}
            </div>
            <div
              className="hidden lg:flex justify-center bottom-0 cursor-pointer flex-none p-2 md:p-3 border-t border-secondary/10 bg-secondary-bg"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              <div
                className={`${
                  !isCollapsed ? "w-11/12" : "w-8/12"
                } p-2 m-auto border border-secondary/20 rounded-lg 
       bg-secondary-bg/50 flex justify-center items-center space-x-2 text-sm font-medium text-secondary hover:bg-secondary-bg transition-colors`}
              >
                <GetHeroIcon
                  name={
                    isCollapsed
                      ? "ArrowRightEndOnRectangleIcon"
                      : "ArrowLeftEndOnRectangleIcon"
                  }
                  className="w-4 h-4"
                  aria-hidden="true"
                  variant="outline"
                />
                {!isCollapsed && (
                  <span className="hidden md:inline">
                    {t("assessment.question.collapse")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex w-full min-w-0 flex-1 lg:min-h-0">
            {questionType === "coding" && (
              <AssessmentCodingQuestion
                question={targetQuestion}
                sectionId={targetSection.sectionId}
                totalQuestionsInSection={targetSection.totalQuestionCount}
                changeActiveQuestion={changeActiveQuestion}
              />
            )}
            {questionType == "objective" && (
              <AssessmentObjectiveQuestions
                question={targetQuestion}
                sectionId={targetSection.sectionId}
                totalQuestionsInSection={targetSection.totalQuestionCount}
                changeActiveQuestion={changeActiveQuestion}
                targetSection={targetSection}
              />
            )}
            {questionType == "subjective" && (
              <AssessmentSubjectiveQuestion
                question={targetQuestion}
                sectionId={targetSection.sectionId}
                totalQuestionsInSection={targetSection.totalQuestionCount}
                changeActiveQuestion={changeActiveQuestion}
              />
            )}
          </div>
        </div>
      </div>
      <Modal
        open={showSummaryModal}
        dialogueClassName="w-full max-w-[95vw] max-h-[90dvh] overflow-y-auto xl:min-w-[1100px] xl:p-8"
        childDialogTitle={t("assessment.question.sectionSummary")}
        closeBtn
      >
        <SectionSummaryModal targetSection={targetSection} />
      </Modal>
    </>
  );
};

export default Question;
